import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

const lpStyles = `
@keyframes lp-fade-up {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lp-glow-pulse {
  0%, 100% { opacity: 0.45; }
  50%      { opacity: 0.8; }
}
@keyframes lp-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-8px); }
}
@keyframes lp-sparkle {
  0%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); }
  50%      { opacity: 1; transform: scale(1) rotate(180deg); }
}
`;

const useReveal = (): { ref: React.RefObject<HTMLDivElement>; visible: boolean } => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -30px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
};

const RevealSection = ({
  children,
  className = '',
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) => {
  const { ref, visible } = useReveal();

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      {children}
    </div>
  );
};

const FeatureCard = ({
  title,
  description,
  icon,
  stagger,
  visible,
}: {
  title: string;
  description: string;
  icon: ReactNode;
  stagger: number;
  visible: boolean;
}) => (
  <div
    className="rounded-2xl p-6 transition-all duration-300"
    style={{
      backgroundColor: 'var(--color-bg-surface)',
      border: '1px solid var(--color-border)',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.6s cubic-bezier(0.16,1,0.3,1) ${stagger * 100}ms, transform 0.6s cubic-bezier(0.16,1,0.3,1) ${stagger * 100}ms`,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)';
      e.currentTarget.style.borderColor = 'var(--color-accent)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
      e.currentTarget.style.borderColor = 'var(--color-border)';
    }}
  >
    <div
      className="flex h-10 w-10 items-center justify-center rounded-xl text-lg"
      style={{ backgroundColor: 'var(--color-accent-soft)', color: 'var(--color-accent)' }}
    >
      {icon}
    </div>
    <h3 className="mt-4 text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
      {title}
    </h3>
    <p className="mt-2 text-sm leading-6" style={{ color: 'var(--color-text-secondary)' }}>
      {description}
    </p>
  </div>
);

const AgentToolCard = ({
  name,
  description,
  stagger,
  visible,
}: {
  name: string;
  description: string;
  stagger: number;
  visible: boolean;
}) => (
  <div
    className="flex gap-4"
    style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateX(0)' : 'translateX(-20px)',
      transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${stagger * 150}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${stagger * 150}ms`,
    }}
  >
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
      style={{ backgroundColor: 'var(--color-accent)', color: 'var(--color-accent-fg)' }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    </div>
    <div>
      <h4 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
        {name}
      </h4>
      <p className="mt-1 text-sm leading-6" style={{ color: 'var(--color-text-secondary)' }}>
        {description}
      </p>
    </div>
  </div>
);

const GanttBar = ({
  label,
  w,
  offset,
  delay,
  visible,
}: {
  label: string;
  w: string;
  offset: string;
  delay: number;
  visible: boolean;
}) => (
  <div className="flex items-center gap-2">
    <span
      className="w-20 shrink-0 text-right text-[9px]"
      style={{ color: 'var(--color-text-tertiary)' }}
    >
      {label}
    </span>
    <div className="relative flex-1">
      <div
        className="h-4 rounded"
        style={{
          width: w,
          marginLeft: offset,
          backgroundColor: 'var(--color-accent)',
          opacity: 0.75,
          transformOrigin: 'left',
          transform: visible ? 'scaleX(1)' : 'scaleX(0)',
          transition: `transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        }}
      />
    </div>
  </div>
);

export const LandingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('landing');
  const { ref: agentRef, visible: agentVisible } = useReveal();
  const { ref: featRef, visible: featVisible } = useReveal();
  const { ref: ctaRef, visible: ctaVisible } = useReveal();

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--color-bg-app)' }}>
      <style>{lpStyles}</style>

      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center gap-3 px-6 py-3"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--color-bg-app) 85%, transparent)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid color-mix(in srgb, var(--color-border) 50%, transparent)',
        }}
      >
        <svg width="28" height="28" viewBox="0 0 256 256" fill="none">
          <rect width="256" height="256" rx="52" fill="#0F172A" />
          <line x1="40" y1="80" x2="216" y2="80" stroke="#1E293B" strokeWidth="1.5" />
          <line x1="40" y1="128" x2="216" y2="128" stroke="#1E293B" strokeWidth="1.5" />
          <line x1="40" y1="176" x2="216" y2="176" stroke="#1E293B" strokeWidth="1.5" />
          <rect x="52" y="66" width="100" height="28" rx="8" fill="#3B82F6" opacity="0.85" />
          <rect x="80" y="114" width="120" height="28" rx="8" fill="#8B5CF6" opacity="0.85" />
          <rect x="60" y="162" width="80" height="28" rx="8" fill="#06B6D4" opacity="0.85" />
        </svg>
        <span
          className="text-sm font-semibold tracking-tight"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Timeline
        </span>
        <div className="flex-1" />
        <LanguageSwitcher />
      </header>

      <section className="relative flex flex-col items-center overflow-hidden px-6 pt-28 pb-12 text-center">
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full blur-[120px]"
          style={{
            backgroundColor: 'var(--color-accent)',
            opacity: 0.08,
          }}
        />

        <h1
          className="max-w-2xl text-4xl font-bold leading-snug sm:text-5xl sm:leading-[1.3]"
          style={{
            color: 'var(--color-text-primary)',
            animation: 'lp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 120ms both',
          }}
        >
          {t('hero.title1')}
          <br />
          <span style={{ color: 'var(--color-accent)' }}>{t('hero.title2')}</span>
        </h1>

        <p
          className="mt-6 max-w-lg text-base leading-7"
          style={{
            color: 'var(--color-text-secondary)',
            animation: 'lp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 240ms both',
          }}
        >
          {t('hero.subtitle')}
        </p>

        <button
          type="button"
          className="relative mt-10 overflow-hidden rounded-xl px-8 py-3.5 text-sm font-semibold transition-all duration-200"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-accent-fg)',
            animation: 'lp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 360ms both',
            boxShadow: '0 0 24px color-mix(in srgb, var(--color-accent) 30%, transparent)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 0 40px color-mix(in srgb, var(--color-accent) 50%, transparent)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 0 24px color-mix(in srgb, var(--color-accent) 30%, transparent)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onClick={() => {
            navigate('/timelines');
          }}
        >
          {t('hero.cta')}
        </button>

        <div
          className="mt-16 w-full max-w-4xl overflow-hidden rounded-2xl border"
          style={{
            borderColor: 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)',
            boxShadow: '0 8px 60px color-mix(in srgb, var(--color-accent) 15%, transparent)',
            animation: 'lp-fade-up 0.7s cubic-bezier(0.16,1,0.3,1) 480ms both',
          }}
        >
          <img
            src={i18n.language === 'en' ? '/timeline_en.png' : '/timeline_jp.png'}
            alt={t('hero.screenshotAlt')}
            className="block w-full"
          />
        </div>
      </section>

      <section className="relative mx-auto max-w-5xl px-6 pt-10 pb-20">
        <div ref={agentRef} />

        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-[500px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[160px]"
          style={{
            backgroundColor: 'var(--color-accent)',
            opacity: agentVisible ? 0.06 : 0,
            transition: 'opacity 1.2s ease',
          }}
        />

        <div className="relative">
          <div className="flex flex-col items-center text-center">
            <div
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em]"
              style={{
                backgroundColor: 'var(--color-accent-soft)',
                color: 'var(--color-accent)',
                opacity: agentVisible ? 1 : 0,
                transform: agentVisible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'all 0.5s cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
              AI Agent Compatible
            </div>
            <h2
              className="mt-6 text-3xl font-bold leading-tight sm:text-4xl"
              style={{
                color: 'var(--color-text-primary)',
                opacity: agentVisible ? 1 : 0,
                transform: agentVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 100ms',
              }}
            >
              {t('agent.title1')}
              <br />
              {t('agent.title2')}
            </h2>
            <p
              className="mt-4 max-w-xl text-base leading-7"
              style={{
                color: 'var(--color-text-secondary)',
                opacity: agentVisible ? 1 : 0,
                transform: agentVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: 'all 0.6s cubic-bezier(0.16,1,0.3,1) 200ms',
              }}
            >
              {t('agent.description')}
            </p>
          </div>

          <div
            className="mx-auto mt-12 h-px max-w-xs"
            style={{
              background: 'linear-gradient(90deg, transparent, var(--color-border), transparent)',
              opacity: agentVisible ? 1 : 0,
              transition: 'opacity 0.6s ease 300ms',
            }}
          />

          <div className="mt-14 grid grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <h3
                className="text-lg font-semibold"
                style={{
                  color: 'var(--color-text-primary)',
                  opacity: agentVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease 300ms',
                }}
              >
                {t('agent.toolsTitle')}
              </h3>
              <div className="mt-6 space-y-6">
                <AgentToolCard
                  name={t('agent.claudeCode')}
                  description={t('agent.claudeCodeDesc')}
                  stagger={1}
                  visible={agentVisible}
                />
                <AgentToolCard
                  name={t('agent.openaiCodex')}
                  description={t('agent.openaiCodexDesc')}
                  stagger={2}
                  visible={agentVisible}
                />
                <AgentToolCard
                  name={t('agent.otherAgents')}
                  description={t('agent.otherAgentsDesc')}
                  stagger={3}
                  visible={agentVisible}
                />
              </div>
            </div>

            <div>
              <h3
                className="text-lg font-semibold"
                style={{
                  color: 'var(--color-text-primary)',
                  opacity: agentVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease 300ms',
                }}
              >
                {t('agent.useCaseTitle')}
              </h3>
              <div
                className="mt-6 rounded-2xl p-5"
                style={{
                  backgroundColor: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  opacity: agentVisible ? 1 : 0,
                  transform: agentVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.6s ease 400ms, transform 0.6s ease 400ms',
                }}
              >
                <div className="flex items-center gap-2 text-[10px] font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 17 10 11 4 5" />
                    <line x1="12" y1="19" x2="20" y2="19" />
                  </svg>
                  {t('agent.promptLabel')}
                </div>
                <div className="mt-3 space-y-2.5">
                  {[
                    t('agent.example1'),
                    t('agent.example2'),
                    t('agent.example3'),
                  ].map((example, index) => (
                    <div
                      key={example}
                      className="rounded-lg px-3.5 py-2.5 text-xs leading-5 transition-colors duration-200"
                      style={{
                        backgroundColor: 'var(--color-bg-surface)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border)',
                        opacity: agentVisible ? 1 : 0,
                        transform: agentVisible ? 'translateY(0)' : 'translateY(8px)',
                        transition: `opacity 0.4s ease ${500 + index * 80}ms, transform 0.4s ease ${500 + index * 80}ms`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-accent)';
                        e.currentTarget.style.backgroundColor = 'var(--color-accent-soft)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-surface)';
                      }}
                    >
                      {example}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[10px] font-medium"
                    style={{
                      backgroundColor: 'var(--color-bg-hover)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    {t('agent.tagJson')}
                  </div>
                  <div
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[10px] font-medium"
                    style={{
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--color-accent-fg)',
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="4 17 10 11 4 5" />
                      <line x1="12" y1="19" x2="20" y2="19" />
                    </svg>
                    {t('agent.tagAgent')}
                  </div>
                </div>
              </div>

              <div
                className="mt-4 rounded-2xl p-5"
                style={{
                  backgroundColor: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border)',
                  opacity: agentVisible ? 1 : 0,
                  transform: agentVisible ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'opacity 0.6s ease 600ms, transform 0.6s ease 600ms',
                }}
              >
                <div className="flex items-center gap-2 text-[10px] font-medium" style={{ color: 'var(--color-text-tertiary)' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {t('agent.resultTitle')}
                </div>
                <div className="mt-3 space-y-1.5">
                  {[
                    { label: t('agent.gantt1'), w: '40%', offset: '0%' },
                    { label: t('agent.gantt2'), w: '30%', offset: '20%' },
                    { label: t('agent.gantt3'), w: '50%', offset: '30%' },
                    { label: t('agent.gantt4'), w: '25%', offset: '65%' },
                  ].map((bar, index) => (
                    <GanttBar
                      key={bar.label}
                      label={bar.label}
                      w={bar.w}
                      offset={bar.offset}
                      delay={700 + index * 120}
                      visible={agentVisible}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16">
        <div ref={featRef} />
        <RevealSection>
          <p
            className="text-center text-[11px] font-semibold uppercase tracking-[0.24em]"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            Features
          </p>
          <h2
            className="mt-3 text-center text-2xl font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t('features.title')}
          </h2>
        </RevealSection>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={<span>&#9776;</span>}
            title={t('features.sectionTitle')}
            description={t('features.sectionDesc')}
            stagger={0}
            visible={featVisible}
          />
          <FeatureCard
            icon={<span>&#8634;</span>}
            title={t('features.undoTitle')}
            description={t('features.undoDesc')}
            stagger={2}
            visible={featVisible}
          />
          <FeatureCard
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                <line x1="6" y1="6" x2="6.01" y2="6" />
                <line x1="6" y1="18" x2="6.01" y2="18" />
              </svg>
            }
            title={t('features.localTitle')}
            description={t('features.localDesc')}
            stagger={3}
            visible={featVisible}
          />
        </div>
      </section>

      <section className="flex flex-col items-center px-6 py-16">
        <div ref={ctaRef} />
        <div
          className="relative w-full max-w-lg overflow-hidden rounded-2xl p-8 text-center"
          style={{
            backgroundColor: 'var(--color-bg-surface)',
            border: '1px solid var(--color-border)',
            boxShadow: ctaVisible ? '0 0 48px color-mix(in srgb, var(--color-accent) 12%, transparent)' : 'none',
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
            transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div
            className="pointer-events-none absolute -top-20 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full blur-[80px]"
            style={{
              backgroundColor: 'var(--color-accent)',
              opacity: 0.1,
              animation: ctaVisible ? 'lp-glow-pulse 3s ease-in-out infinite' : 'none',
            }}
          />
          <h2 className="relative text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t('cta.title')}
          </h2>
          <p className="relative mt-3 text-sm leading-6" style={{ color: 'var(--color-text-secondary)' }}>
            {t('cta.description')}
          </p>
          <button
            type="button"
            className="relative mt-6 w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'var(--color-accent-fg)',
              boxShadow: '0 0 20px color-mix(in srgb, var(--color-accent) 25%, transparent)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 36px color-mix(in srgb, var(--color-accent) 45%, transparent)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 20px color-mix(in srgb, var(--color-accent) 25%, transparent)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onClick={() => {
              navigate('/timelines');
            }}
          >
            {t('cta.button')}
          </button>
        </div>
      </section>

      <footer className="px-6 py-8 text-center">
        <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
          &copy; {new Date().getFullYear()} Timeline
        </p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <Link
            to="/terms"
            className="text-[11px] hover:opacity-80 transition-opacity"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {t('footer.terms')}
          </Link>
          <Link
            to="/privacy"
            className="text-[11px] hover:opacity-80 transition-opacity"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {t('footer.privacy')}
          </Link>
          <a
            href="https://forms.gle/CPqR873eWPZBAGem9"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] hover:opacity-80 transition-opacity"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {t('footer.contact')}
          </a>
        </div>
      </footer>
    </div>
  );
};
